import clsx, { ClassValue } from "clsx";
import { useEffect, useRef, useState } from "react";

type SN = string | number;

type WindowSize = {
  width: number | undefined;
  height: number | undefined;
};

type UnsafeText = SN | string | null | undefined;
export function detectCard(event: any) {
  const rawValue = event.target.value.replace(/\s/g, "");
  //   const maskedValue = rawValue.replace(/(\d{1,4})/g, "$1 ").trim();

  // Detect card type
  if (/^4/.test(rawValue)) {
    return "Visa";
  } else if (/^5[1-5]/.test(rawValue)) {
    return "Mastercard";
  } else if (/^3[47]/.test(rawValue)) {
    return "American Express";
  } else if (/^6(?:011|5)/.test(rawValue)) {
    return "Discover";
  } else if (/^5[6-9]|^506[0-9]|^6500[0-9]/.test(rawValue)) {
    return "Verve";
  } else {
    return "";
  }
}


export function formatCard(event: any) {
  const rawValue = event.target.value.replace(/\s/g, "");

  return rawValue.replace(/(\d{1,4})/g, "$1 ").trim();
}

export function handleExpiryDateChange(event: any) {
  // Get the input value without slashes
  const rawValue = event.target.value.replace(/\//g, "");

  // Add slash after the first two digits
  const maskedValue = rawValue.replace(/^(\d{1,2})/, "$1/").substr(0, 7);

  // Set the input value to the masked version
  return maskedValue;
}

export function handleCvvChange(event: any) {
  // Get the input value without spaces
  const rawValue = event.target.value.replace(/\s/g, "");

  // Replace all digits with asterisks except the last 3
  const maskedValue = rawValue.replace(/\d(?=\d{3})/g, "*");
  // Set the input value to the masked version
  return maskedValue;
}

interface FormatOptions {
  hideDecimal?: boolean;
  hideSymbol?: boolean;
  convertToNumber?: boolean;
  symbol?: string;
  removeLeadingZero?: boolean;
  numberAbbreviate?: boolean;
}
const formatNumberWithSymbols = (number: number) => {
  const formats = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "B" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "Q" },
    { value: 1e18, symbol: "QT" },
    { value: 1e21, symbol: "QQ" },
  ];
  const format = formats
    .slice()
    .reverse()
    .find((fmt) => number >= fmt.value);
  if (format) {
    return (
      (number / format.value).toFixed(2).replace(/\.00$/, "") + format.symbol
    );
  }
  return number.toString();
};
const getCurrencySymbol = (param: string) => {
  const nairaSymbol = "\u{020A6}";
  const upperParam = String(param).toUpperCase();
  return upperParam === "NGN" || upperParam === "NAIRA"
    ? nairaSymbol
    : upperParam === "USD" || upperParam === "DOLLAR"
    ? "$"
    : upperParam === "EUR" || upperParam === "EURO"
    ? "€"
    : upperParam === "ZAR"
    ? "R"
    : upperParam === "KES"
    ? "KSh"
    : upperParam === "CAD"
    ? "$"
    : upperParam === "GHS"
    ? "₵"
    : upperParam === "GBP" || upperParam === "POUNDS" || upperParam === "POUND"
    ? "£"
    : param;
};
export const RavenNumberFormat = (
  number: number | string | null | undefined,
  options?: FormatOptions
): string => {
  const symbol = getCurrencySymbol(options?.symbol || "NGN");
  const hideDecimal = options?.hideDecimal || false;
  const hideSymbol = options?.hideSymbol || false;
  const convertToNumber = options?.convertToNumber || false;
  const removeLeadingZero = options?.removeLeadingZero || false;
  const numberAbbreviate = options?.numberAbbreviate || false;
  if (number !== null && number !== undefined) {
    // Remove symbols and non-numeric characters
    const numericValue: string = number.toString().replace(/[^0-9.]/g, "");
    // Separate the integer and decimal parts
    let [integerPart, decimalPart] = numericValue.split(".");
    if (removeLeadingZero) {
      // Remove leading zeros if more than one leading zero
      if (/^0{2,}/.test(integerPart)) {
        integerPart = integerPart.replace(/^0+/, "");
      }
    }
    if (convertToNumber) {
      // Return number without thousand formatting and without symbol
      const val = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
      return val;
    }
    const formattedValue = Number(
      integerPart + (decimalPart ? "." + decimalPart : "")
    );
    if (numberAbbreviate) {
      const formatted = formatNumberWithSymbols(formattedValue);
      return hideSymbol ? formatted : `${symbol}${formatted}`;
    }
    // Format the integer part with thousand separator
    integerPart = Number(integerPart).toLocaleString();
    // Handle the decimal part
    if (!hideDecimal) {
      if (decimalPart) {
        // Convert the decimal part back to a number and round to two significant figures
        decimalPart = Number("0." + decimalPart)
          .toPrecision(2)
          .split(".")[1];
        // Remove trailing zeros from the decimal part
        decimalPart = decimalPart?.replace(/0+$/, "") || "";
      }
      const val = decimalPart
        ? `${integerPart}${decimalPart ? "." : ""}${decimalPart}`
        : `${integerPart}.00`;
      return hideSymbol ? val : `${symbol}${val}`;
    } else {
      return hideSymbol ? integerPart : `${symbol}${integerPart}`;
    }
  } else {
    return hideSymbol ? `0.00` : `${symbol}0.00`;
  }
};

export const RavenTextTrim = (str, num) => {
  if (str && num) {
    const val =
      String(str).length > Number(num || 15)
        ? `${String(str).slice(0, Number(num || 15))}...`
        : str;
    return val;
  }
};

export const RavenFormatMetaData = (obj) => {
  if (typeof obj !== "string") {
    return obj;
  }
  if (obj) {
    const newObj = JSON?.parse(obj);
    // console.log(newObj);
    return newObj;
  }
};

export const RavenCopyFunction = async (item) => {
  const flattenObject = (obj) => {
    return Object.entries(obj).flatMap(([key, value]) => {
      // Check if value is a stringified object (e.g. meta_data)
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (typeof parsed === "object" && parsed !== null) {
            return flattenObject(parsed); // Exclude parent key
          }
        } catch (_) {
          // Not a stringified object
        }
      }
      // If nested object, recurse
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        return flattenObject(value);
      }
      return `${key}: ${value}`;
    });
  };
  let formatted = "";
  if (typeof item === "string" || typeof item === "number") {
    formatted = item.toString();
  } else if (Array.isArray(item)) {
    formatted = item
      .map((obj) => {
        if (
          typeof obj === "object" &&
          obj !== null &&
          "label" in obj &&
          "value" in obj
        ) {
          return `${obj.label}: ${obj.value}`;
        }
        return "";
      })
      .filter(Boolean)
      .join("\n");
  } else if (typeof item === "object" && item !== null) {
    const flattened = flattenObject(item);
    formatted = flattened.join("\n");
  } else {
    console.error("Unsupported value for clipboard copy.");
    return;
  }
  try {
    await navigator.clipboard.writeText(formatted);
  } catch (err) {
    console.error("Clipboard write failed:", err);
  }
};

export const RavenUseOnClickOutside = <T = any>(handler: (e: MouseEvent) => void) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const listener = (event: any) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || (ref.current as any).contains(event.target)) {
        return;
      }

      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref]);

  return ref;
};

export function RavenCleanLeadingZeros(input: string): string {
  if (input) {
    // Trim any whitespace
    const trimmed = String(input)?.trim();
    // If it's an empty string, return it
    if (trimmed === "") return "";
    // If it's a valid decimal like "0.5", "0.01", allow as-is
    if (/^0\.\d+$/.test(trimmed)) return trimmed;
    // If it's all zeros like "0000", "00", reduce to single "0"
    if (/^0+$/.test(trimmed)) return "0";
    // If it's like "01", "0004", remove leading zeros
    return trimmed.replace(/^0+/, "");
  } else {
    return "";
  }
}


type AreEqualParams = {
  paramOne?: any;
  paramTwo?: any;
  checkForEmpty?: boolean;
  ignoreKeys?: string[];
};
/**
 * Recursively checks if a value is considered empty:
 * - null or undefined
 * - string empty after trim
 * - array with length < 1 or all elements empty
 * - object with no keys or all keys empty values
 */
function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    // Check if every element is empty
    return value.every(isEmpty);
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    if (keys.length === 0) return true;
    // Check if every key's value is empty
    return keys.every((key) => isEmpty(value[key]));
  }
  return false;
}
/**
 * Checks if two values are equal under custom rules.
 */
export function isTrulyEmpty(input: any, ignoreKeys: string[] = []): boolean {
  // null or undefined
  if (input === null || input === undefined) return true;
  // string: check after trim
  if (typeof input === 'string') {
    return input.trim() === '';
  }
  // array: check if empty
  if (Array.isArray(input)) {
    return input.length === 0;
  }
  // object: check if any non-ignored key has empty value
  if (typeof input === 'object') {
    for (const key in input) {
      if (ignoreKeys.includes(key)) continue;
      const value = input[key];
      if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return true;
      }
    }
  }
  // default: not empty
  return false;
}
export function onlyOneParamExists(paramOne?: any, paramTwo?: any): boolean {
  const isOneDefined = typeof paramOne !== 'undefined';
  const isTwoDefined = typeof paramTwo !== 'undefined';
  return (isOneDefined && !isTwoDefined) || (!isOneDefined && isTwoDefined);
}
export function RavenValidateData({
  paramOne,
  paramTwo,
  checkForEmpty = true,
  ignoreKeys = [],
}: AreEqualParams): boolean {
  // Handle case where only one param is passed
  if (onlyOneParamExists(paramOne, paramTwo)) {
    const singleParam = paramOne !== undefined ? paramOne : paramTwo;
    return isTrulyEmpty(singleParam, ignoreKeys);
  }
  // If neither param is provided
  const bothProvided =
    typeof paramOne !== 'undefined' && typeof paramTwo !== 'undefined';
  if (!bothProvided) {
    return checkForEmpty ? false : true;
  }
  // Check if paramOne has any empty value (ignoring ignoreKeys)
  if (
    checkForEmpty &&
    paramOne &&
    typeof paramOne === 'object' &&
    paramTwo &&
    typeof paramTwo === 'object' &&
    !Array.isArray(paramOne)
  ) {
    for (const key of Object.keys(paramOne)) {
      if (ignoreKeys.includes(key)) continue;
      if (isTrulyEmpty(paramOne[key])) return true;
    }
  }
  // If one param is empty and the other is not
  if (checkForEmpty && (isTrulyEmpty(paramOne, ignoreKeys) || isTrulyEmpty(paramTwo, ignoreKeys))) {
    return false;
  }
  // Strict equality
  if (paramOne === paramTwo) return true;
  // Compare primitives as strings
  if (
    typeof paramOne !== 'object' || paramOne === null ||
    typeof paramTwo !== 'object' || paramTwo === null
  ) {
    return String(paramOne) === String(paramTwo);
  }
  // Arrays
  if (Array.isArray(paramOne) && Array.isArray(paramTwo)) {
    if (paramOne.length !== paramTwo.length) return false;
    for (let i = 0; i < paramOne.length; i++) {
      if (
        !RavenValidateData({
          paramOne: paramOne[i],
          paramTwo: paramTwo[i],
          checkForEmpty,
          ignoreKeys,
        })
      ) {
        return false;
      }
    }
    return true;
  }
  // Objects
  if (!Array.isArray(paramOne) && !Array.isArray(paramTwo)) {
    const keys1 = Object.keys(paramOne).filter(k => !ignoreKeys.includes(k));
    const keys2 = Object.keys(paramTwo).filter(k => !ignoreKeys.includes(k));
    const allKeys: string[] = Array.from(new Set([...keys1, ...keys2]));
    for (const key of allKeys) {
      if (ignoreKeys.includes(key)) continue;
      if (
        !RavenValidateData({
          paramOne: paramOne[key],
          paramTwo: paramTwo[key],
          checkForEmpty,
          ignoreKeys,
        })
      ) {
        return false;
      }
    }
    return true;
  }
  // Mismatched types (e.g., one is array, the other is object)
  return false;
}

export function RavenClearLocalStorage(keys?: string | string[]) {
  // If no keys provided or empty array, clear everything
  if (!keys || (Array.isArray(keys) && keys.length === 0)) {
    localStorage.clear();
    return;
  }
  const preserved: Record<string, string> = {};
  const keyList = Array.isArray(keys) ? keys : [keys];
  // Store all values to preserve
  keyList.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      preserved[key] = value;
    }
  });
  // Clear all localStorage
  localStorage.clear();
  // Restore preserved values
  Object.entries(preserved).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });
}

export function RavenGetQueryValue(variable: string): string | false {
  const query = window.location.search.substring(1);
  const vars = query.split("&");
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split("=");
    if (pair[0] === variable) {
      const value = decodeURIComponent(pair[1] || "")
        .replace(/\s+/g, "") // remove whitespace
        .replace(/"/g, ""); // remove double quotes
      return value;
    }
  }
  return false;
}


export function RavenWindowSize(): WindowSize {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    // Add listener if in browser
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      handleResize(); // Call once on mount
    }
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return windowSize;
}

export function RavenRemoveSpace(str: string): string {
  return String(str).replace(/\s+/g, "");
}

export function RavenRemoveEmptyValue<T extends Record<string, any>>(obj: T): Partial<T> {
  // Create a new object to store the filtered properties
  const filteredObj: Partial<T> = {};

  // Iterate over each property in the object
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      // Check if the value is not empty
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0) &&
        !(
          typeof value === "object" &&
          !Array.isArray(value) &&
          Object.keys(value).length === 0
        )
      ) {
        // Add the property to the filtered object
        filteredObj[key] = value;
      }
    }
  }

  return filteredObj;
}

export function RavenRenderConditionally(label: any, data: any) {
  if (Boolean(data)) {
    return [{ label: label, value: data }];
  } else {
    return [];
  }
}

export function RavenFilterEmptyColumns<T extends Record<string, any>>(
  obj: T
): Partial<T> {
  // Create a new object to store the filtered properties
  const filteredObj: Partial<T> = {};

  // Iterate over each property in the object
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      // Check if the value is not empty
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0) &&
        !(
          typeof value === "object" &&
          !Array.isArray(value) &&
          value !== null &&
          Object.keys(value).length === 0
        )
      ) {
        // Add the property to the filtered object
        filteredObj[key] = value;
      }
    }
  }

  return filteredObj;
}

export const trimLongString = (
  str: string | undefined,
  num: number
): string => {
  if (str && num) {
    return str.length > num ? `${str.slice(0, num)}...` : str;
  }
  return ""; // Return an empty string if str is undefined or num is 0
};

export const handleCopy = (data: string, setState: any, stateValue?: any) => {
  navigator.clipboard.writeText(data);

  setState(stateValue || true);

  setTimeout(() => {
    setState(false);
  }, 1000);
  // You can add any additional logic or feedback here, like showing a tooltip or toast message.
};

export const cn = (...inputs: ClassValue[]) => clsx(inputs);

export function createStyleCSSProperty(name: string, gap: SN) {
  if (typeof gap === "string") return { [name]: gap } as React.CSSProperties;

  return { [name]: `${gap}rem` } as React.CSSProperties;
}

export function createStyleCSSJustifyProperty(name: string, gap: any) {
  if (typeof gap === "string") return { [name]: gap } as React.CSSProperties;

  return { [name]: `${gap}` } as React.CSSProperties;
}

export const safeValue = (str: UnsafeText): string => {
  if (!str || str.toString().trim().length === 0) {
    return "--";
  } else {
    return str.toString();
  }
};
export const safeText = (
  str: IsUncertain<UnsafeText>,
  /** Value to trim string by, ideal length is 20 */
  trim?: number
): string => {
  const text = safeValue(str);

  if (trim) return trimLongString(text, trim) ?? "--";

  return text || "--";
};

type NestedObject = { [key: string]: any };
type ValueObject = { key: string; value?: any };

function parseIfJsonString(value: any): any {
  if (typeof value !== "string") return value;
  try {
    const parsedValue = JSON.parse(value);
    // Check if the result is an object or an array, which are valid JSON structures
    if (typeof parsedValue === "object" && parsedValue !== null) {
      return parsedValue;
    }
    // Return the original value if parsing was successful but the result is not an object
    return value;
  } catch {
    // If an error occurs, return the original value, indicating it's not a JSON string
    return value;
  }
}

function findValueInNestedObject(obj: any, keyPath: string): any {
  if (!keyPath || typeof keyPath !== "string") return "";
  const keys = keyPath.split(".").map((k) => k.replace("?", ""));
  let currentValue = obj;
  for (const key of keys) {
    if (
      currentValue &&
      typeof currentValue === "object" &&
      key in currentValue
    ) {
      currentValue = parseIfJsonString(currentValue[key]);
    } else if (obj?.default !== undefined) {
      return obj.default; // return default if defined
    } else {
      return ""; // fallback
    }
  }
  return currentValue;
}

/**
 * Maps values from an array of objects based on a values object, updating empty values.
 * @param arrayOfObjects - The array of objects to map.
 * @param valuesObject - The object containing values for keys.
 * @returns An array of objects with updated values.
 */
export function RavenGetValue(
  arrayOfObjects: ValueObject[],
  valuesObject: NestedObject
): ValueObject[] {
  return arrayOfObjects.map((item) => {
    // Only update if the current value is empty or undefined
    const newValue =
      item.value === "" || item.value === undefined
        ? findValueInNestedObject(valuesObject, item.key)
        : item.value;
    return {
      ...item,
      value: newValue,
    };
  });
}

interface searchOption {
  search: string;
  list: any[];
  keysToSearch: string[];
  reArrange: boolean;
}
export function RavenSearch(options: searchOption) {
  const param = options?.search || "";
  const keysToSearch = options?.keysToSearch || [""];
  const list = options?.list || [];
  const lowerCaseValue = param.toLowerCase();
  const rearrangeList = options?.reArrange;
  if (rearrangeList) {
    const compareFunction = (a: any, b: any) => {
      const aContainsSearchValue = keysToSearch?.some((key) =>
        a[key]?.toLowerCase()?.includes(lowerCaseValue)
      );
      const bContainsSearchValue = keysToSearch?.some((key) =>
        b[key]?.toLowerCase()?.includes(lowerCaseValue)
      );
      if (aContainsSearchValue && !bContainsSearchValue) {
        return -1; // Move a before b
      } else if (!aContainsSearchValue && bContainsSearchValue) {
        return 1; // Move b before a
      } else {
        return 0; // Leave the order unchanged
      }
    };
    const sortedArray = list?.slice()?.sort(compareFunction);
    // console.log(sortedArray);
    return sortedArray;
  } else {
    const filteredArray = list?.filter((item) =>
      keysToSearch?.some((key) =>
        item[key]?.toLowerCase()?.includes(lowerCaseValue)
      )
    );
    return filteredArray;
  }
}